import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a", amberText: "#92400e",
  blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:    <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:   <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    ChevronRight: <><path d="m9 18 6-6-6-6"/></>,
    ChevronUp:    <><path d="m18 15-6-6-6 6"/></>,
    Shield:       <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    Cookie:       <><path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/><path d="M8.5 8.5v.01"/><path d="M16 15.5v.01"/><path d="M12 12v.01"/></>,
    ToggleLeft:   <><rect width="20" height="12" x="2" y="6" rx="6"/><circle cx="8" cy="12" r="2"/></>,
    ToggleRight:  <><rect width="20" height="12" x="2" y="6" rx="6"/><circle cx="16" cy="12" r="2"/></>,
    CheckCircle:  <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Info:         <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>,
    Lock:         <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    BarChart:     <><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></>,
    Settings:     <><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></>,
    Zap:          <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Globe:        <><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></>,
    Mail:         <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ─────────────────────────────────────────
   COOKIE CATEGORIES DATA
───────────────────────────────────────── */

const COOKIE_CATEGORIES = [
  {
    id: "essential",
    icon: "Lock",
    label: "Strictly necessary",
    color: T.g500,
    colorBg: T.g50,
    colorBorder: T.g100,
    required: true,
    defaultOn: true,
    summary: "These cookies are required for JiraniApp to function. They cannot be switched off.",
    purpose: "Strictly necessary cookies keep you logged in, protect your session, remember your language preference, and allow core platform features like booking, payments, and account management to work. Without these, JiraniApp cannot operate.",
    examples: [
      { name: "session_id", duration: "Session", purpose: "Maintains your login session while you are using the platform." },
      { name: "csrf_token", duration: "Session", purpose: "Protects form submissions and booking actions from cross-site request forgery." },
      { name: "auth_token", duration: "30 days", purpose: "Keeps you securely logged in across visits without re-entering your credentials." },
      { name: "locale_pref", duration: "1 year", purpose: "Remembers your language and region preferences." },
    ],
  },
  {
    id: "functional",
    icon: "Settings",
    label: "Functional",
    color: T.blue,
    colorBg: T.blueBg,
    colorBorder: T.blueBorder,
    required: false,
    defaultOn: true,
    summary: "These cookies remember your preferences and make the platform easier to use.",
    purpose: "Functional cookies save your choices so you do not have to repeat them. They remember things like which tab you last viewed, your preferred booking filters, and notification settings. Disabling these does not break the platform, but you may need to re-set preferences each visit.",
    examples: [
      { name: "filter_prefs", duration: "90 days", purpose: "Saves your last-used search filters such as service category, price range, and distance." },
      { name: "ui_state",     duration: "30 days", purpose: "Remembers which sections of your dashboard you have expanded or collapsed." },
      { name: "notif_prefs",  duration: "1 year",  purpose: "Stores your notification preferences so they apply consistently across sessions." },
    ],
  },
  {
    id: "analytics",
    icon: "BarChart",
    label: "Analytics",
    color: T.amber,
    colorBg: T.amberBg,
    colorBorder: T.amberBorder,
    required: false,
    defaultOn: true,
    summary: "These cookies help us understand how the platform is used so we can improve it.",
    purpose: "Analytics cookies collect anonymised data about how visitors navigate JiraniApp — which pages are visited most, where people drop off during booking, and how features are used. This data never identifies you personally. We use it to fix problems, improve slow pages, and make booking flows easier. We do not sell this data to third parties.",
    examples: [
      { name: "_jrn_session", duration: "30 minutes", purpose: "Tracks anonymised page visits and user flow within a single session." },
      { name: "_jrn_ref",     duration: "7 days",     purpose: "Records how you arrived at JiraniApp (e.g. a search engine or a link) to help us understand which channels bring clients and Pros to the platform." },
      { name: "_jrn_perf",    duration: "1 year",     purpose: "Measures page load times and error rates to help the engineering team identify and fix performance issues." },
    ],
  },
  {
    id: "marketing",
    icon: "Globe",
    label: "Marketing",
    color: "#7c3aed",
    colorBg: "#fdf4ff",
    colorBorder: "#e9d5ff",
    required: false,
    defaultOn: false,
    summary: "These cookies may be used to show you relevant content about JiraniApp on other platforms.",
    purpose: "Marketing cookies allow us to measure the effectiveness of any promotional campaigns JiraniApp runs on external platforms. They may track whether you visited JiraniApp after seeing an advertisement. These cookies do not track your activity on other websites beyond a campaign interaction. We do not currently run behavioural advertising, but if we do in the future, we will update this policy and require your active consent.",
    examples: [
      { name: "_jrn_campaign", duration: "30 days", purpose: "Records which campaign or promotional link brought you to JiraniApp, helping us measure whether campaigns are effective." },
    ],
  },
];

/* ─────────────────────────────────────────
   SECTIONS FOR TOC
───────────────────────────────────────── */

const SECTIONS = [
  { id: "what",       num: "01", title: "What are cookies" },
  { id: "how",        num: "02", title: "How JiraniApp uses cookies" },
  { id: "types",      num: "03", title: "Types of cookies we use" },
  { id: "control",    num: "04", title: "Your choices and controls" },
  { id: "third",      num: "05", title: "Third-party cookies" },
  { id: "updates",    num: "06", title: "Updates to this policy" },
  { id: "contact",    num: "07", title: "Contact us" },
];

/* ─────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────── */

/* Preference toggle row */
function PreferenceRow({ category, enabled, onToggle }) {
  const isRequired = category.required;
  return (
    <div style={{
      padding: "20px 0",
      borderBottom: `1px solid ${T.n100}`,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div style={{ display: "flex", gap: 14, flex: 1 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: category.colorBg, border: `1px solid ${category.colorBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name={category.icon} size={17} color={category.color} strokeWidth={1.7} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.n900 }}>{category.label}</span>
              {isRequired && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: T.g600,
                  background: T.g50, border: `1px solid ${T.g100}`,
                  borderRadius: 99, padding: "2px 7px", letterSpacing: "0.05em",
                }}>Always on</span>
              )}
            </div>
            <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.65, margin: 0 }}>{category.summary}</p>
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={() => !isRequired && onToggle(category.id)}
          aria-label={`${enabled ? "Disable" : "Enable"} ${category.label} cookies`}
          style={{
            flexShrink: 0,
            width: 48, height: 28, borderRadius: 99,
            border: "none", cursor: isRequired ? "not-allowed" : "pointer",
            background: enabled ? T.g500 : T.n200,
            position: "relative",
            transition: "background .2s",
            opacity: isRequired ? 0.75 : 1,
          }}>
          <span style={{
            position: "absolute",
            top: 4, left: enabled ? 24 : 4,
            width: 20, height: 20,
            borderRadius: "50%",
            background: T.white,
            boxShadow: "0 1px 4px rgba(0,0,0,.15)",
            transition: "left .2s cubic-bezier(.4,0,.2,1)",
          }} />
        </button>
      </div>
    </div>
  );
}

/* Cookie table */
function CookieTable({ examples }) {
  return (
    <div style={{ overflowX: "auto", marginTop: 16 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: T.sans }}>
        <thead>
          <tr style={{ background: T.n50, borderBottom: `1px solid ${T.n150}` }}>
            {["Cookie name", "Duration", "What it does"].map((h, i) => (
              <th key={i} style={{
                padding: "10px 14px", textAlign: "left",
                fontSize: 11, fontWeight: 700, color: T.n400,
                letterSpacing: "0.07em", textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {examples.map((row, i) => (
            <tr key={i} style={{
              borderBottom: `1px solid ${T.n100}`,
              background: i % 2 === 0 ? T.white : T.n50,
            }}>
              <td style={{ padding: "12px 14px", fontFamily: "monospace", fontSize: 12, color: T.n800, whiteSpace: "nowrap" }}>
                {row.name}
              </td>
              <td style={{ padding: "12px 14px", color: T.n600, whiteSpace: "nowrap" }}>
                {row.duration}
              </td>
              <td style={{ padding: "12px 14px", color: T.n600, lineHeight: 1.6 }}>
                {row.purpose}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* Category section with expandable table */
function CategoryDetail({ category }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderRadius: 14,
      border: `1.5px solid ${open ? category.colorBorder : T.n150}`,
      background: T.white,
      overflow: "hidden",
      transition: "border-color .2s",
      marginBottom: 12,
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", background: open ? category.colorBg : T.white,
        border: "none", cursor: "pointer",
        padding: "20px 22px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, textAlign: "left", fontFamily: T.sans,
        transition: "background .2s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11, flexShrink: 0,
            background: open ? T.white : T.n50,
            border: `1.5px solid ${open ? category.colorBorder : T.n150}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all .2s",
          }}>
            <Icon name={category.icon} size={18} color={open ? category.color : T.n400} strokeWidth={1.6} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, letterSpacing: "-0.01em" }}>
              {category.label}
            </div>
            <div style={{ fontSize: 12, color: T.n400, marginTop: 2 }}>
              {category.examples.length} cookie{category.examples.length !== 1 ? "s" : ""} · {category.required ? "Required" : "Optional"}
            </div>
          </div>
        </div>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          background: open ? category.color : T.n100,
          border: `1px solid ${open ? category.color : T.n200}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .2s",
        }}>
          <Icon name="ChevronRight" size={13}
            color={open ? T.white : T.n400}
            strokeWidth={2.5}
            style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .2s" }} />
        </span>
      </button>

      <div style={{
        maxHeight: open ? 800 : 0,
        overflow: "hidden",
        transition: "max-height .38s cubic-bezier(.4,0,.2,1)",
      }}>
        <div style={{ padding: "0 22px 22px" }}>
          <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.8, margin: "0 0 4px" }}>
            {category.purpose}
          </p>
          <CookieTable examples={category.examples} />
        </div>
      </div>
    </div>
  );
}

/* Section wrapper */
function Section({ id, num, title, children }) {
  return (
    <div id={id} style={{ paddingTop: 56, scrollMarginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 18 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: T.g500,
          letterSpacing: "0.1em", textTransform: "uppercase",
          fontFamily: T.sans,
        }}>{num}</span>
        <h2 style={{
          fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,28px)", fontWeight: 400,
          color: T.n900, margin: 0, letterSpacing: "-0.025em", lineHeight: 1.2,
        }}>{title}</h2>
      </div>
      <div style={{ borderTop: `2px solid ${T.n100}`, paddingTop: 24 }}>
        {children}
      </div>
    </div>
  );
}

function LegalP({ children, style = {} }) {
  return (
    <p style={{
      fontSize: 15, color: T.n600, lineHeight: 1.85,
      margin: "0 0 16px", fontFamily: T.sans, ...style,
    }}>{children}</p>
  );
}

function LegalList({ items }) {
  return (
    <ul style={{ margin: "0 0 16px", paddingLeft: 0, listStyle: "none" }}>
      {items.map((item, i) => (
        <li key={i} style={{
          display: "flex", gap: 12, alignItems: "flex-start",
          fontSize: 15, color: T.n600, lineHeight: 1.8,
          marginBottom: 10, fontFamily: T.sans,
        }}>
          <span style={{ marginTop: 6, flexShrink: 0 }}>
            <Icon name="ChevronRight" size={13} color={T.g400} strokeWidth={2.5} />
          </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

/* ══ MAIN PAGE ══ */
export default function CookiePolicyPage() {
  useFonts();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("what");

  /* Preferences state */
  const initialPrefs = Object.fromEntries(
    COOKIE_CATEGORIES.map(c => [c.id, c.defaultOn])
  );
  const [prefs, setPrefs] = useState(initialPrefs);
  const [prefsSaved, setPrefsSaved] = useState(false);

  const togglePref = (id) => {
    setPrefs(p => ({ ...p, [id]: !p[id] }));
    setPrefsSaved(false);
  };

  const savePrefs = () => {
    setPrefsSaved(true);
    setTimeout(() => setPrefsSaved(false), 3000);
  };

  /* TOC active tracking */
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
        .jrn-legal-link { color: ${T.g600}; text-decoration: none; border-bottom: 1px solid ${T.g200}; transition: color .15s, border-color .15s; }
        .jrn-legal-link:hover { color: ${T.g700}; border-color: ${T.g600}; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "none", border: "none", cursor: "pointer",
          color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <span style={{ fontSize: 12, color: T.n400, fontFamily: T.sans }}>Legal</span>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(48px,7vw,80px) 20px clamp(40px,5vw,60px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 22 }}>
            <Icon name="Cookie" size={14} color={T.g500} strokeWidth={2.2} />
            Legal
          </div>
          <h1 style={{
            fontFamily: T.serif, fontSize: "clamp(40px,7vw,64px)", fontWeight: 400,
            margin: "0 0 16px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em",
          }}>
            Cookie{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>Policy</em>
          </h1>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.7, maxWidth: 500 }}>
            This policy explains what cookies JiraniApp uses, why we use them, and how you can control them.
            We have kept this as plain and specific as possible.
          </p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 99, background: T.n50, border: `1px solid ${T.n150}` }}>
            <Icon name="Info" size={13} color={T.n400} strokeWidth={2} />
            <span style={{ fontSize: 12, color: T.n500, fontFamily: T.sans }}>Last updated: 14 June 2026</span>
          </div>
        </div>
      </div>

      {/* ══ BODY: TOC + CONTENT ══ */}
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "0 20px 100px", display: "flex", gap: 60, alignItems: "flex-start" }}>

        {/* ── Sticky TOC (desktop) ── */}
        <div style={{
          position: "sticky", top: 32, flexShrink: 0, width: 200,
          paddingTop: 48, display: "none",
        }}
          ref={el => {
            if (el) {
              const show = () => el.style.display = window.innerWidth >= 900 ? "block" : "none";
              show();
              window.addEventListener("resize", show);
            }
          }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>
            Contents
          </div>
          <nav>
            {SECTIONS.map(s => {
              const active = activeSection === s.id;
              return (
                <a key={s.id} href={`#${s.id}`}
                  onClick={e => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" }); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 0", textDecoration: "none",
                    borderLeft: `2px solid ${active ? T.g500 : T.n150}`,
                    paddingLeft: 14, marginLeft: -2,
                    transition: "border-color .18s",
                  }}>
                  <span style={{
                    fontSize: 11, color: active ? T.g600 : T.n400,
                    fontWeight: active ? 600 : 400,
                    fontFamily: T.sans, lineHeight: 1.4,
                    transition: "color .18s",
                  }}>{s.num}</span>
                  <span style={{
                    fontSize: 13, color: active ? T.n900 : T.n500,
                    fontWeight: active ? 600 : 400,
                    fontFamily: T.sans, lineHeight: 1.4,
                    transition: "color .18s",
                  }}>{s.title}</span>
                </a>
              );
            })}
          </nav>
        </div>

        {/* ── Main content ── */}
        <div style={{ flex: 1, maxWidth: 720 }}>

          {/* ── 01 What are cookies ── */}
          <Section id="what" num="01" title="What are cookies">
            <LegalP>
              Cookies are small text files that a website saves to your browser when you visit. They are
              stored on your device and sent back to the website on future visits. Cookies serve several
              purposes — keeping you logged in, remembering your preferences, and helping us understand
              how the platform is used.
            </LegalP>
            <LegalP>
              Some cookies are set by JiraniApp directly. Others may be set by third-party services we
              use — such as analytics or payment providers. Each type is explained in this policy.
            </LegalP>
            <div style={{
              padding: "16px 20px", borderRadius: 12,
              background: T.g50, border: `1px solid ${T.g100}`,
              display: "flex", gap: 12, alignItems: "flex-start", marginTop: 4,
            }}>
              <Icon name="Info" size={16} color={T.g600} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <p style={{ margin: 0, fontSize: 14, color: T.g700, lineHeight: 1.7, fontFamily: T.sans }}>
                JiraniApp does not use cookies to track your activity across other websites, build
                advertising profiles, or sell your data to third parties. Our cookie use is limited to
                what is necessary to run the platform and understand how it is used.
              </p>
            </div>
          </Section>

          {/* ── 02 How JiraniApp uses cookies ── */}
          <Section id="how" num="02" title="How JiraniApp uses cookies">
            <LegalP>
              We use cookies for four specific purposes on JiraniApp:
            </LegalP>
            <LegalList items={[
              "To keep you securely logged in and protect your account and booking activity.",
              "To remember your preferences so the platform works the way you expect it to across visits.",
              "To collect anonymised usage data that helps us improve the platform, fix problems, and build better features.",
              "To measure the reach and effectiveness of any promotional campaigns we run, so we can invest in what works.",
            ]} />
            <LegalP>
              We do not use cookies to show you targeted advertising based on your behaviour elsewhere
              on the internet. We do not sell, rent, or share cookie data with advertising networks.
            </LegalP>
          </Section>

          {/* ── 03 Types of cookies ── */}
          <Section id="types" num="03" title="Types of cookies we use">
            <LegalP>
              The cookies JiraniApp sets fall into four categories. Expand each one to see the specific
              cookies, what they do, and how long they last.
            </LegalP>
            <div style={{ marginTop: 8 }}>
              {COOKIE_CATEGORIES.map(cat => (
                <CategoryDetail key={cat.id} category={cat} />
              ))}
            </div>
          </Section>

          {/* ── 04 Your choices ── */}
          <Section id="control" num="04" title="Your choices and controls">
            <LegalP>
              You have control over the optional cookies JiraniApp sets. Strictly necessary cookies
              cannot be disabled because they are required for the platform to function. For all other
              categories, you can toggle your preferences below and save your choices.
            </LegalP>

            {/* Preference panel */}
            <div style={{
              borderRadius: 16,
              border: `1.5px solid ${T.n150}`,
              overflow: "hidden",
              marginTop: 24, marginBottom: 24,
            }}>
              <div style={{
                padding: "18px 22px",
                background: T.n50,
                borderBottom: `1px solid ${T.n150}`,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <Icon name="Settings" size={16} color={T.n500} strokeWidth={1.7} />
                <span style={{ fontSize: 14, fontWeight: 600, color: T.n800, fontFamily: T.sans }}>
                  Your cookie preferences
                </span>
              </div>
              <div style={{ padding: "0 22px" }}>
                {COOKIE_CATEGORIES.map(cat => (
                  <PreferenceRow
                    key={cat.id}
                    category={cat}
                    enabled={prefs[cat.id]}
                    onToggle={togglePref}
                  />
                ))}
              </div>
              <div style={{
                padding: "16px 22px",
                background: T.n50,
                borderTop: `1px solid ${T.n150}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, flexWrap: "wrap",
              }}>
                <span style={{ fontSize: 13, color: T.n400, fontFamily: T.sans }}>
                  Changes take effect on your next page load.
                </span>
                <button onClick={savePrefs} style={{
                  padding: "10px 22px",
                  background: prefsSaved ? T.g100 : T.g500,
                  color: prefsSaved ? T.g700 : T.white,
                  border: `1.5px solid ${prefsSaved ? T.g200 : T.g500}`,
                  borderRadius: 9, fontSize: 13, fontWeight: 600,
                  cursor: "pointer", fontFamily: T.sans,
                  display: "inline-flex", alignItems: "center", gap: 7,
                  transition: "all .2s",
                }}>
                  {prefsSaved
                    ? <><Icon name="CheckCircle" size={14} color={T.g600} strokeWidth={2} /> Preferences saved</>
                    : "Save my preferences"}
                </button>
              </div>
            </div>

            <LegalP>
              You can also manage cookies directly through your browser settings. Most browsers allow you
              to block all cookies, delete existing cookies, or be notified when a cookie is set. Note
              that blocking strictly necessary cookies will prevent JiraniApp from working correctly.
            </LegalP>
            <LegalP>
              Links to cookie management guides for common browsers:
            </LegalP>
            <LegalList items={[
              <><a className="jrn-legal-link" href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer">Google Chrome</a> — Settings › Privacy and security › Cookies and other site data</>,
              <><a className="jrn-legal-link" href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noreferrer">Mozilla Firefox</a> — Settings › Privacy and Security › Cookies and Site Data</>,
              <><a className="jrn-legal-link" href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noreferrer">Safari</a> — Preferences › Privacy › Cookies and website data</>,
              <><a className="jrn-legal-link" href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noreferrer">Microsoft Edge</a> — Settings › Cookies and site permissions › Cookies and site data</>,
            ]} />
          </Section>

          {/* ── 05 Third-party ── */}
          <Section id="third" num="05" title="Third-party cookies">
            <LegalP>
              JiraniApp uses a small number of third-party services that may set their own cookies on
              your device. These services are selected carefully and are bound by their own privacy
              policies. We do not grant third parties permission to use your data for their own purposes
              beyond the service they provide to us.
            </LegalP>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8, marginBottom: 16 }}>
              {[
                {
                  name: "M-Pesa / payment processor",
                  purpose: "Our payment processor sets session cookies during the payment flow to protect your transaction. These cookies are deleted once payment is complete.",
                  icon: "Zap",
                },
                {
                  name: "Analytics provider",
                  purpose: "We use an analytics service to collect anonymised usage data. This service sets cookies only if you have accepted analytics cookies in your preferences above. No personally identifiable data is collected or shared.",
                  icon: "BarChart",
                },
              ].map((item, i) => (
                <div key={i} style={{
                  padding: "18px 20px", borderRadius: 12,
                  border: `1px solid ${T.n150}`, background: T.n50,
                  display: "flex", gap: 14, alignItems: "flex-start",
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                    background: T.white, border: `1px solid ${T.n150}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon name={item.icon} size={16} color={T.n500} strokeWidth={1.7} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 5 }}>{item.name}</div>
                    <div style={{ fontSize: 14, color: T.n600, lineHeight: 1.7 }}>{item.purpose}</div>
                  </div>
                </div>
              ))}
            </div>
            <LegalP>
              We do not use Facebook Pixel, Google Ads tracking, or any other behavioural advertising
              tools. If this changes in the future, we will update this policy and seek your explicit
              consent before enabling any such tracking.
            </LegalP>
          </Section>

          {/* ── 06 Updates ── */}
          <Section id="updates" num="06" title="Updates to this policy">
            <LegalP>
              We may update this Cookie Policy from time to time to reflect changes in the cookies we
              use, the services we rely on, or applicable laws. When we make material changes, we will
              update the date at the top of this page and, where appropriate, notify you through the
              platform or by email.
            </LegalP>
            <LegalP>
              We encourage you to check this page periodically. Continued use of JiraniApp after an
              update constitutes acceptance of the revised policy, except where your active consent is
              required under applicable law.
            </LegalP>
          </Section>

          {/* ── 07 Contact ── */}
          <Section id="contact" num="07" title="Contact us">
            <LegalP>
              If you have questions about how JiraniApp uses cookies, or if you want to exercise any
              rights in relation to your data, please contact us at{" "}
              <a className="jrn-legal-link" href="mailto:privacy@jiraniapp.com">privacy@jiraniapp.com</a>{" "}
              or through the{" "}
              <a className="jrn-legal-link" href="/help">Help and Support</a> page.
            </LegalP>
            <LegalP>
              We aim to respond to all privacy-related enquiries within 5 business days.
            </LegalP>
          </Section>

          {/* ── Related legal docs ── */}
          <div style={{
            marginTop: 64, padding: "clamp(24px,4vw,36px)", borderRadius: 16,
            border: `1px solid ${T.n150}`, background: T.n50,
          }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 16px" }}>
              Related legal documents
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {[
                { label: "Terms of Service",    path: "/terms",   desc: "The rules governing your use of the JiraniApp platform." },
                { label: "Privacy Policy",      path: "/privacy", desc: "How we collect, use, and protect your personal data." },
              ].map((doc, i, arr) => (
                <a key={i} href={doc.path}
                  onClick={e => { e.preventDefault(); navigate(doc.path); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 16, padding: "16px 0",
                    borderBottom: i < arr.length - 1 ? `1px solid ${T.n150}` : "none",
                    textDecoration: "none",
                  }}
                  onMouseEnter={e => e.currentTarget.querySelector(".lbl").style.color = T.g600}
                  onMouseLeave={e => e.currentTarget.querySelector(".lbl").style.color = T.n900}>
                  <div>
                    <div className="lbl" style={{ fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 3, transition: "color .15s", fontFamily: T.sans }}>
                      {doc.label}
                    </div>
                    <div style={{ fontSize: 13, color: T.n400, fontFamily: T.sans }}>{doc.desc}</div>
                  </div>
                  <Icon name="ChevronRight" size={16} color={T.n300} strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}